/**
 * Regex Assist Engine
 *
 * Client-side regex suggestion engine: takes valid/invalid sample values
 * and produces a regex pattern + plain-English description.
 */
function classOf(ch) {
    if (ch >= '0' && ch <= '9')
        return 'digit';
    if (ch >= 'A' && ch <= 'Z')
        return 'upper';
    if (ch >= 'a' && ch <= 'z')
        return 'lower';
    return 'literal';
}
/**
 * Tokenize a string into runs of the same character class.
 * Consecutive literal characters that differ are kept as one segment
 * only if they are identical across all valid samples (handled later).
 * Here each literal run consists of identical repeated chars or a
 * contiguous block of non-alnum chars.
 */
function tokenize(value) {
    const segs = [];
    let i = 0;
    while (i < value.length) {
        const cls = classOf(value[i]);
        if (cls === 'literal') {
            // Collect consecutive literal characters as one segment
            let lit = '';
            while (i < value.length && classOf(value[i]) === 'literal') {
                lit += value[i];
                i++;
            }
            segs.push({ cls: 'literal', literal: lit, len: lit.length });
        }
        else {
            let len = 0;
            while (i < value.length && classOf(value[i]) === cls) {
                len++;
                i++;
            }
            segs.push({ cls, literal: '', len });
        }
    }
    return segs;
}
function generalize(samples) {
    if (samples.length === 0)
        return null;
    const first = tokenize(samples[0]);
    const result = first.map((s) => ({
        cls: s.cls,
        literal: s.literal,
        minLen: s.len,
        maxLen: s.len,
    }));
    for (let i = 1; i < samples.length; i++) {
        const segs = tokenize(samples[i]);
        if (segs.length !== result.length)
            return null; // incompatible structure
        for (let j = 0; j < result.length; j++) {
            const r = result[j];
            const s = segs[j];
            if (r.cls !== s.cls)
                return null; // different character class
            if (r.cls === 'literal' && r.literal !== s.literal)
                return null; // different literal
            r.minLen = Math.min(r.minLen, s.len);
            r.maxLen = Math.max(r.maxLen, s.len);
        }
    }
    return result;
}
/* ------------------------------------------------------------------ */
/*  Regex building                                                    */
/* ------------------------------------------------------------------ */
/** Escape regex-special characters so a literal string matches itself.
 *  Exported so derived-validation's mask→regex converter shares one escape
 *  table (no divergence between the two). */
export function escapeRegex(s) {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
function segmentToPattern(seg) {
    if (seg.cls === 'literal') {
        return escapeRegex(seg.literal);
    }
    const charClass = seg.cls === 'digit' ? '\\d' :
        seg.cls === 'upper' ? '[A-Z]' :
            '[a-z]';
    if (seg.minLen === seg.maxLen) {
        return seg.minLen === 1 ? charClass : `${charClass}{${seg.minLen}}`;
    }
    return `${charClass}{${seg.minLen},${seg.maxLen}}`;
}
function buildPattern(segments) {
    return '^' + segments.map(segmentToPattern).join('') + '$';
}
/* ------------------------------------------------------------------ */
/*  suggestRegex                                                      */
/* ------------------------------------------------------------------ */
export function suggestRegex(input) {
    const { valid, invalid } = input;
    if (valid.length === 0) {
        return { pattern: '', description: '' };
    }
    const segments = generalize(valid);
    if (!segments) {
        // Samples have incompatible structure; fall back to alternation
        const alt = valid.map(escapeRegex).join('|');
        const pattern = `^(?:${alt})$`;
        return { pattern, description: describeRegex(pattern) };
    }
    const pattern = buildPattern(segments);
    // Verify against all samples
    const re = new RegExp(pattern);
    const allValidMatch = valid.every((v) => re.test(v));
    const allInvalidReject = invalid.every((v) => !re.test(v));
    if (allValidMatch && allInvalidReject) {
        return { pattern, description: describeRegex(pattern) };
    }
    // If verification fails, fall back to alternation of valid samples
    const alt = valid.map(escapeRegex).join('|');
    const fallback = `^(?:${alt})$`;
    return { pattern: fallback, description: describeRegex(fallback) };
}
/* ------------------------------------------------------------------ */
/*  describeRegex                                                     */
/* ------------------------------------------------------------------ */
/**
 * Simple pattern-description engine. Handles the common constructs
 * that `suggestRegex` produces plus basic hand-written patterns.
 * For anything it can't parse, returns the raw pattern.
 */
export function describeRegex(pattern) {
    // Strip anchors for parsing
    let body = pattern;
    if (body.startsWith('^'))
        body = body.slice(1);
    if (body.endsWith('$'))
        body = body.slice(0, -1);
    const parts = [];
    let i = 0;
    while (i < body.length) {
        // ---- character classes: \d, [A-Z], [a-z] ----
        const ccMatch = matchCharClass(body, i);
        if (ccMatch) {
            const { label, end } = ccMatch;
            const quant = matchQuantifier(body, end);
            parts.push(formatQuant(label, quant.text));
            i = quant.end;
            continue;
        }
        // ---- dot ----
        if (body[i] === '.') {
            const quant = matchQuantifier(body, i + 1);
            parts.push(formatQuant('any character', quant.text));
            i = quant.end;
            continue;
        }
        // ---- escaped literal (\/, \\, \., etc.) ----
        if (body[i] === '\\' && i + 1 < body.length && !/[dDwWsS]/.test(body[i + 1])) {
            let lit = '';
            while (i < body.length && body[i] === '\\' && i + 1 < body.length && !/[dDwWsS]/.test(body[i + 1])) {
                lit += body[i + 1];
                i += 2;
            }
            parts.push(`"${lit}"`);
            continue;
        }
        // ---- non-capturing group (?:...) — skip for description ----
        if (body.slice(i, i + 3) === '(?:') {
            // For alternation groups, just return a simplified description
            return `Matches: ${pattern}`;
        }
        // ---- plain literal characters (letters, digits, common punctuation) ----
        if (/[A-Za-z0-9 \-_/,;:!@#]/.test(body[i])) {
            let lit = '';
            while (i < body.length && /[A-Za-z0-9 \-_/,;:!@#]/.test(body[i])) {
                lit += body[i];
                i++;
            }
            parts.push(`"${lit}"`);
            continue;
        }
        // Something we don't understand — bail
        return `Matches: ${pattern}`;
    }
    if (parts.length === 0)
        return pattern;
    return 'Matches ' + parts.join(', then ');
}
/* ------------------------------------------------------------------ */
/*  describeRegex internals                                           */
/* ------------------------------------------------------------------ */
function matchCharClass(body, i) {
    // \d
    if (body[i] === '\\' && body[i + 1] === 'd') {
        return { label: 'digit', end: i + 2 };
    }
    // \w
    if (body[i] === '\\' && body[i + 1] === 'w') {
        return { label: 'word character', end: i + 2 };
    }
    // \s
    if (body[i] === '\\' && body[i + 1] === 's') {
        return { label: 'whitespace', end: i + 2 };
    }
    // [A-Z]
    if (body.slice(i, i + 5) === '[A-Z]') {
        return { label: 'uppercase letter', end: i + 5 };
    }
    // [a-z]
    if (body.slice(i, i + 5) === '[a-z]') {
        return { label: 'lowercase letter', end: i + 5 };
    }
    // [0-9]
    if (body.slice(i, i + 5) === '[0-9]') {
        return { label: 'digit', end: i + 5 };
    }
    return null;
}
function matchQuantifier(body, i) {
    if (i >= body.length)
        return { text: '', end: i };
    // {N} or {M,N}
    if (body[i] === '{') {
        const close = body.indexOf('}', i);
        if (close !== -1) {
            const inner = body.slice(i + 1, close);
            return { text: `{${inner}}`, end: close + 1 };
        }
    }
    if (body[i] === '+')
        return { text: '+', end: i + 1 };
    if (body[i] === '*')
        return { text: '*', end: i + 1 };
    if (body[i] === '?')
        return { text: '?', end: i + 1 };
    return { text: '', end: i };
}
function formatQuant(label, quant) {
    if (!quant)
        return `1 ${label}`;
    // {N}
    const exactMatch = quant.match(/^\{(\d+)\}$/);
    if (exactMatch) {
        const n = exactMatch[1];
        return `exactly ${n} ${label}s`;
    }
    // {M,N}
    const rangeMatch = quant.match(/^\{(\d+),(\d+)\}$/);
    if (rangeMatch) {
        return `${rangeMatch[1]} to ${rangeMatch[2]} ${label}s`;
    }
    if (quant === '+')
        return `one or more ${label}s`;
    if (quant === '*')
        return `zero or more ${label}s`;
    if (quant === '?')
        return `optional ${label}`;
    return `${label} ${quant}`;
}
//# sourceMappingURL=regex-assist.js.map